/**
 * Workspace Settings Transfer Page
 *
 * "Copy Settings / Paste Settings" for a whole workspace, modeled on Lightroom.
 * A workspace admin exports the workspace's portable configuration as a JSON
 * file (Copy), then on a new workspace imports it (Paste) and chooses which
 * sections to apply — every section is checked by default; uncheck to omit.
 *
 * Only portable, policy-style settings are included; workspace-specific record
 * references are excluded. See lib/workspaceSettingsTransfer.ts.
 */

import { useRef, useState } from 'react';
import { ArrowLeftRight, Download, Upload, Copy, Check, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import {
  exportWorkspaceSettings,
  parseSettingsBundle,
  getPresentSectionKeys,
  summarizeSection,
  applyWorkspaceSettings,
  exportFilename,
  getSectionMeta,
  type WorkspaceSettingsBundle,
  type SettingsSectionKey,
} from '../../../lib/workspaceSettingsTransfer';

export default function WorkspaceSettingsTransfer() {
  const workspaceId = getCurrentWorkspaceId();
  const addNotification = useUIStore((s) => s.addNotification);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Import state
  const [bundle, setBundle] = useState<WorkspaceSettingsBundle | null>(null);
  const [presentKeys, setPresentKeys] = useState<SettingsSectionKey[]>([]);
  const [selected, setSelected] = useState<Set<SettingsSectionKey>>(new Set());
  const [parseError, setParseError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  // ── Export ────────────────────────────────────────────────────────────────

  const doExport = async (): Promise<WorkspaceSettingsBundle | null> => {
    if (!workspaceId) return null;
    const { bundle: b, error } = await exportWorkspaceSettings(workspaceId);
    if (error || !b) {
      addNotification({ type: 'error', title: 'Export failed', message: error ?? 'Unknown error', duration: 5000 });
      return null;
    }
    return b;
  };

  const handleDownload = async () => {
    setExporting(true);
    const b = await doExport();
    setExporting(false);
    if (!b) return;
    const blob = new Blob([JSON.stringify(b, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFilename(b);
    a.click();
    URL.revokeObjectURL(url);
    addNotification({
      type: 'success',
      title: 'Settings exported',
      message: `${getPresentSectionKeys(b).length} section(s) downloaded as ${exportFilename(b)}.`,
      duration: 4000,
    });
  };

  const handleCopy = async () => {
    setExporting(true);
    const b = await doExport();
    setExporting(false);
    if (!b) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(b, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      addNotification({ type: 'error', title: 'Copy failed', message: 'Clipboard unavailable — use Download instead.', duration: 4000 });
    }
  };

  // ── Import — load a bundle ──────────────────────────────────────────────────

  const loadBundleText = (text: string) => {
    const { bundle: b, error } = parseSettingsBundle(text);
    if (error || !b) {
      setBundle(null);
      setPresentKeys([]);
      setSelected(new Set());
      setParseError(error ?? 'Could not read settings file.');
      return;
    }
    const keys = getPresentSectionKeys(b);
    setBundle(b);
    setPresentKeys(keys);
    setSelected(new Set(keys)); // Lightroom default: everything checked.
    setParseError(null);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    loadBundleText(text);
  };

  const toggleSection = (key: SettingsSectionKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearImport = () => {
    setBundle(null);
    setPresentKeys([]);
    setSelected(new Set());
    setParseError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Import — apply ──────────────────────────────────────────────────────────

  const handleApply = async () => {
    if (!workspaceId || !bundle) return;
    const keys = [...selected];
    if (keys.length === 0) {
      addNotification({ type: 'error', title: 'Nothing selected', message: 'Check at least one section to apply.', duration: 4000 });
      return;
    }
    setApplying(true);
    const { applied, errors } = await applyWorkspaceSettings(workspaceId, bundle, keys);
    setApplying(false);

    if (errors.length > 0) {
      addNotification({
        type: applied.length > 0 ? 'warning' : 'error',
        title: applied.length > 0 ? 'Applied with some errors' : 'Import failed',
        message: `${applied.length} section(s) applied. ${errors.length} failed: ${errors.map((e) => getSectionMeta(e.section)?.label ?? e.section).join(', ')}.`,
        duration: 7000,
      });
    } else {
      addNotification({
        type: 'success',
        title: 'Settings applied',
        message: `${applied.length} section(s) imported into this workspace. Reload to see all changes.`,
        duration: 6000,
      });
      clearImport();
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ArrowLeftRight className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Import / Export Settings"
        subtitle="Copy this workspace's configuration to a file, then paste it into a new workspace — choosing exactly which settings to bring over."
      />

      {!workspaceId && (
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:border-[var(--mustard)]/60">
          <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">Select a workspace in the sidebar to export or import settings.</p>
        </div>
      )}

      {workspaceId && (
        <>
          {/* ── Export ───────────────────────────────────────────────────── */}
          <section>
            <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-2">Copy settings from this workspace</h2>
            <div className="card space-y-3">
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                Export this workspace's portable configuration — platform modules, module policies, general
                preferences, governance policies, and the Baseline Template policy — as a JSON file you can import
                into another workspace.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleDownload}
                  disabled={exporting}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  {exporting ? 'Preparing…' : 'Download settings file'}
                </button>
                <button
                  onClick={handleCopy}
                  disabled={exporting}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:hover:bg-[var(--bg-raised)]"
                >
                  {copied ? <Check className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)]" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy to clipboard'}
                </button>
              </div>
            </div>
          </section>

          {/* ── Import ───────────────────────────────────────────────────── */}
          <section>
            <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-2">Paste settings into this workspace</h2>
            <div className="card space-y-4">
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                Load a settings file exported from another workspace. Every section is selected by default —
                uncheck anything you don't want to apply here, then import.
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:hover:bg-[var(--bg-raised)]"
                >
                  <Upload className="h-4 w-4" />
                  Choose settings file…
                </button>
                {bundle && (
                  <button onClick={clearImport} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-pri)] underline">
                    Clear
                  </button>
                )}
              </div>

              {parseError && (
                <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] dark:bg-[var(--coral)]/20 dark:border-[var(--coral)]/60">
                  <AlertTriangle className="h-4 w-4 text-[var(--coral)] dark:text-[var(--coral)] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)]">{parseError}</p>
                </div>
              )}

              {bundle && (
                <>
                  <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    From{' '}
                    <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                      {bundle.source.workspaceName || bundle.source.slug || 'an unnamed workspace'}
                    </span>
                    {bundle.exportedAt && <> · exported {bundle.exportedAt.slice(0, 10)}</>}
                  </div>

                  <div className="space-y-2">
                    {presentKeys.map((key) => {
                      const meta = getSectionMeta(key);
                      const checked = selected.has(key);
                      return (
                        <label
                          key={key}
                          className={`flex items-start gap-3  border p-3 cursor-pointer transition-colors ${
                            checked
                              ? 'border-primary-300 bg-primary-50/40 dark:border-primary-700/50 dark:bg-primary-900/20'
                              : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:hover:border-[var(--rule-bright)]'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSection(key)}
                            className="mt-0.5 h-4 w-4 rounded text-primary-600 focus:ring-primary-500"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{meta?.label ?? key}</span>
                              <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] font-mono">{summarizeSection(bundle, key)}</span>
                            </div>
                            {meta?.description && <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{meta.description}</p>}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <button
                      onClick={handleApply}
                      disabled={applying || selected.size === 0}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      <ArrowLeftRight className="h-4 w-4" />
                      {applying ? 'Applying…' : `Import ${selected.size} section${selected.size === 1 ? '' : 's'}`}
                    </button>
                    <button
                      onClick={() => setSelected(new Set(presentKeys))}
                      className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-pri)]"
                    >
                      Select all
                    </button>
                    <button
                      onClick={() => setSelected(new Set())}
                      className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-pri)]"
                    >
                      Select none
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>

          <div className="card bg-[var(--slate-dim)] border-[var(--slate)] space-y-2 dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]/60">
            <p className="text-sm font-medium text-[var(--text-sec)]">What does and doesn't transfer</p>
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-ter)]/90">
              Portable, policy-style settings transfer: module toggles, module policies, general preferences,
              governance policies, and the Baseline Template policy.
            </p>
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-ter)]/90">
              Workspace-specific data is <strong>not</strong> included — users, items, cycles, the default
              workflow, and connector credentials stay with their original workspace.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
